import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { contactService } from '../api';

// Animations variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const ContactSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.gradient ? 
    `linear-gradient(135deg, ${props.theme.background} 0%, ${props.theme.background}ee 100%)` : 
    props.theme.background};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.accent});
    z-index: 2;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => props.theme.name === 'dark' ? 
      'radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.03) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)' : 
      'radial-gradient(circle at 25% 25%, rgba(0, 0, 0, 0.015) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(0, 0, 0, 0.015) 0%, transparent 50%)'};
    z-index: 1;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3.5rem;
  margin-top: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactForm = styled(motion.form)`
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  box-shadow: ${props => props.theme.card.shadow}, 0 10px 40px -15px ${props => props.theme.primary}30;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'};
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.card.shadow}, 0 15px 50px -12px ${props => props.theme.primary}40;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  font-weight: 600;
  position: relative;
  padding-bottom: 0.75rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.accent});
    border-radius: 3px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.75rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  color: ${props => props.theme.text};
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s ease;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid ${props => props.theme.text}15;
  border-radius: 8px;
  background: ${props => props.theme.name === 'dark' ? props.theme.background : props.theme.card.background}; 
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  transition: all 0.3s ease;
  font-size: 1rem;
  height: 50px;
  
  &::placeholder {
    color: ${props => props.theme.text}70;
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}25;
  }
  
  &:hover {
    border-color: ${props => props.theme.primary}80;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid ${props => props.theme.text}15;
  border-radius: 8px;
  background: ${props => props.theme.name === 'dark' ? props.theme.background : props.theme.card.background}; 
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  transition: all 0.3s ease;
  font-size: 1rem;
  min-height: 170px;
  resize: vertical;
  line-height: 1.6;
  
  &::placeholder {
    color: ${props => props.theme.text}70;
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}25;
  }
  
  &:hover {
    border-color: ${props => props.theme.primary}80;
  }
`;

const ErrorMessage = styled(motion.div)`
  color: ${props => props.theme.error};
  font-size: 0.85rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  
  &::before {
    content: '⚠️';
    margin-right: 0.4rem;
    font-size: 0.9rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.success}, ${props => props.theme.success}dd);
  color: white;
  padding: 1.2rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  font-weight: 500;
  box-shadow: 0 10px 30px -10px ${props => props.theme.success}80;
  
  &::before {
    content: '✓';
    margin-right: 0.8rem;
    font-size: 1.2rem;
    background: white;
    color: ${props => props.theme.success};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }
`;

const ErrorNotification = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.error}, ${props => props.theme.error}dd);
  color: white;
  padding: 1.2rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  font-weight: 500;
  box-shadow: 0 10px 30px -10px ${props => props.theme.error}80;
  
  &::before {
    content: '!';
    margin-right: 0.8rem;
    font-size: 1.2rem;
    background: white;
    color: ${props => props.theme.error};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }
`;

const ContactInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 2.5rem;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.card.shadow}, 0 15px 50px -12px ${props => props.theme.primary}30;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, ${props => props.theme.primary}, ${props => props.theme.accent});
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  font-weight: 600;
  position: relative;
  padding-bottom: 0.75rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.accent});
    border-radius: 3px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  i {
    color: ${props => props.theme.primary};
    margin-right: 1.2rem;
    font-size: 1.5rem;
    margin-top: 0.2rem;
    background: ${props => props.theme.name === 'dark' ? props.theme.background : `${props.theme.primary}15`};
    padding: 0.8rem;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 5px 15px -5px ${props => props.theme.primary}40;
    transition: all 0.3s ease;
  }
  
  &:hover i {
    transform: scale(1.05);
    box-shadow: 0 8px 20px -5px ${props => props.theme.primary}60;
  }
  
  div {
    color: ${props => props.theme.text};
    flex: 1;
    line-height: 1.7;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.primary}, ${props => props.theme.accent});
  color: white;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px -5px ${props => props.theme.primary}80;
  
  &:hover {
    transform: translateY(-5px) scale(1.1);
    box-shadow: 0 12px 20px -5px ${props => props.theme.primary}80;
  }
  
  &:active {
    transform: translateY(0) scale(0.95);
  }
`;

const MapContainer = styled(motion.div)`
  height: 320px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  border: 1px solid ${props => props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.accent});
    z-index: 2;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
`;

const StyledButton = styled(Button)`
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 0;
    border-radius: 100%;
    transform: scale(1, 1) translate(-50%);
    transform-origin: 50% 50%;
  }

  &:focus:not(:active)::after {
    animation: ripple 1s ease-out;
  }

  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 0.5;
    }
    20% {
      transform: scale(25, 25);
      opacity: 0.3;
    }
    100% {
      opacity: 0;
      transform: scale(40, 40);
    }
  }
`;

// Composant de label flottant séparé pour éviter les problèmes de sélecteur
const FloatingLabel = styled.span`
  position: absolute;
  left: 15px;
  top: ${props => props.active ? '-10px' : '15px'};
  font-size: ${props => props.active ? '0.8rem' : '1rem'};
  padding: 0 5px;
  background: ${props => props.theme.card.background};
  color: ${props => props.active ? props.theme.primary : `${props.theme.text}80`};
  transition: all 0.2s ease;
  pointer-events: none;
  border-radius: 4px;
  z-index: 1;
`;

const ContactPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [focusedFields, setFocusedFields] = useState({});
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };
  
  // Validation schema pour le formulaire
  const validationSchema = Yup.object({
    name: Yup.string().required(t('nameRequired')),
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    subject: Yup.string().required(t('subjectRequired')),
    message: Yup.string().required(t('messageRequired')).min(10, t('messageMinLength')),
  });
  
  // Configuration Formik
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Envoyer le formulaire à l'API Django
        await contactService.create(values);
        
        // Réinitialiser le formulaire et afficher le message de succès
        setFormSubmitted(true);
        setSubmitError(null);
        resetForm();
        setFocusedFields({});
        
        // Cacher le message de succès après 5 secondes
        setTimeout(() => {
          setFormSubmitted(false);
        }, 5000);
      } catch (error) {
        console.error("Erreur lors de l'envoi du formulaire:", error);
        
        // Afficher un message d'erreur
        setSubmitError(
          error.response?.data?.message || 
          t('formSubmissionError') || 
          "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });
  
  const handleFocus = (field) => {
    setFocusedFields(prev => ({ ...prev, [field]: true }));
  };
  
  const handleBlur = (e) => {
    const field = e.target.name;
    formik.handleBlur(e);
    if (!formik.values[field]) {
      setFocusedFields(prev => ({ ...prev, [field]: false }));
    }
  };
  
  // Effect to set initial focused state based on values
  useEffect(() => {
    const initialFocusedState = {};
    Object.keys(formik.values).forEach(key => {
      initialFocusedState[key] = !!formik.values[key];
    });
    setFocusedFields(initialFocusedState);
  }, []);
  
  return (
    <ContactSection theme={theme}>
      <Container>
        <SectionTitle
          title={t('contactUs')}
          subtitle={t('contactUsSubtitle')}
        />
        
        <ContactGrid as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeInUp}>
            <ContactForm 
              onSubmit={formik.handleSubmit}
              theme={theme}
            >
              <FormTitle theme={theme}>{t('getInTouch')}</FormTitle>
              
              <AnimatePresence>
                {formSubmitted && (
                  <SuccessMessage
                    theme={theme}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {t('formSubmissionSuccess')}
                  </SuccessMessage>
                )}
              </AnimatePresence>
              
              <AnimatePresence>
                {submitError && (
                  <ErrorNotification
                    theme={theme}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {submitError}
                  </ErrorNotification>
                )}
              </AnimatePresence>
              
              <FormGroup>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  theme={theme}
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus('name')}
                  value={formik.values.name}
                />
                <FloatingLabel 
                  theme={theme} 
                  active={focusedFields.name || !!formik.values.name}
                >
                  {t('name')}
                </FloatingLabel>
                <AnimatePresence>
                  {formik.touched.name && formik.errors.name && (
                    <ErrorMessage 
                      theme={theme}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {formik.errors.name}
                    </ErrorMessage>
                  )}
                </AnimatePresence>
              </FormGroup>
              
              <FormGroup>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  theme={theme}
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus('email')}
                  value={formik.values.email}
                />
                <FloatingLabel 
                  theme={theme} 
                  active={focusedFields.email || !!formik.values.email}
                >
                  {t('email')}
                </FloatingLabel>
                <AnimatePresence>
                  {formik.touched.email && formik.errors.email && (
                    <ErrorMessage 
                      theme={theme}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {formik.errors.email}
                    </ErrorMessage>
                  )}
                </AnimatePresence>
              </FormGroup>
              
              <FormGroup>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  theme={theme}
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus('phone')}
                  value={formik.values.phone}
                />
                <FloatingLabel 
                  theme={theme} 
                  active={focusedFields.phone || !!formik.values.phone}
                >
                  {t('phone')} ({t('optional')})
                </FloatingLabel>
              </FormGroup>
              
              <FormGroup>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  theme={theme}
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus('subject')}
                  value={formik.values.subject}
                />
                <FloatingLabel 
                  theme={theme} 
                  active={focusedFields.subject || !!formik.values.subject}
                >
                  {t('subject')}
                </FloatingLabel>
                <AnimatePresence>
                  {formik.touched.subject && formik.errors.subject && (
                    <ErrorMessage 
                      theme={theme}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {formik.errors.subject}
                    </ErrorMessage>
                  )}
                </AnimatePresence>
              </FormGroup>
              
              <FormGroup>
                <TextArea
                  id="message"
                  name="message"
                  theme={theme}
                  onChange={formik.handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus('message')}
                  value={formik.values.message}
                />
                <FloatingLabel 
                  theme={theme} 
                  active={focusedFields.message || !!formik.values.message}
                >
                  {t('message')}
                </FloatingLabel>
                <AnimatePresence>
                  {formik.touched.message && formik.errors.message && (
                    <ErrorMessage 
                      theme={theme}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {formik.errors.message}
                    </ErrorMessage>
                  )}
                </AnimatePresence>
              </FormGroup>
              
              <ButtonWrapper>
                <StyledButton
                  type="submit"
                  variant="primary"
                  size="large"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? (
                    <React.Fragment>
                      <span className="spinner"></span> {t('sending')}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <i className="fas fa-paper-plane" style={{ marginRight: '0.5rem' }}></i> {t('sendMessage')}
                    </React.Fragment>
                  )}
                </StyledButton>
              </ButtonWrapper>
            </ContactForm>
          </motion.div>
          
          <ContactInfoContainer>
            <InfoCard 
              theme={theme}
              variants={fadeInUp}
            >
              <InfoTitle theme={theme}>{t('contactInfo')}</InfoTitle>
              
              <InfoItem theme={theme}>
                <i className="fas fa-map-marker-alt" />
                <div>
                  <strong>Château Ngoa-Ekelle</strong><br />
                  Yaoundé, Cameroun
                </div>
              </InfoItem>
              
              <InfoItem theme={theme}>
                <i className="fas fa-phone" />
                <div>
                  <strong>+237 690 232 120 </strong><br />
                  <strong>+237 659 471 152 </strong><br />
                </div>
              </InfoItem>
              
              <InfoItem theme={theme}>
                <i className="fas fa-envelope" />
                <div>
                  <strong>{t('emailUs')}:</strong><br />
                  propentatech@gmail.com
                </div>
              </InfoItem>
              
              <SocialLinks>
                <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer" theme={theme} aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </SocialLink>
                <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer" theme={theme} aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </SocialLink>
                <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer" theme={theme} aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </SocialLink>
                <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" theme={theme} aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </SocialLink>
              </SocialLinks>
            </InfoCard>
            
            <InfoCard 
              theme={theme}
              variants={fadeInUp}
            >
              <InfoTitle theme={theme}>{t('businessHours')}</InfoTitle>
              
              <InfoItem theme={theme}>
                <i className="fas fa-clock" />
                <div>
                  <strong>{t('mondayFriday')}:</strong> 8:00 - 18:00<br />
                  <strong>{t('saturday')}:</strong> 9:00 - 14:00<br />
                  <strong>{t('sunday')}:</strong> {t('closed')}
                </div>
              </InfoItem>
            </InfoCard>
            
            <MapContainer
              theme={theme}
              variants={fadeInUp}
            >
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d63692.99402649872!2d11.4606324!3d3.8504673!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x108bcf1e8e88686d%3A0xfca8362744a0eb3d!2sUniversit%C3%A9%20de%20Yaound%C3%A9%201-%20NGOA%20EKELLE!5e0!3m2!1sfr!2scm!4v1745766529979!5m2!1sfr!2scm"
                title="Propenta Location"
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </MapContainer>
          </ContactInfoContainer>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default ContactPage;